import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  PAGE, CONTENT_W, COLORS, text, wrapText, drawHeader, drawFooters,
  sectionHeading, checkPageBreak, drawKpiGrid, drawColumnChart,
  drawHorizontalBarChart, directionColor, safeText, fmtDate,
} from "./pdfHelpers";
import { humanize, fmtCompact, fmtPercent, fmtNumber, directionWord, buildScopeDescription } from "./textHelpers";

const TITLE = "Sales Analysis Report";

export function buildSalesAnalysisReportPdf(metrics, findings, interpretations, content, { watermark = true } = {}) {

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const { meta } = metrics;

  // ================= PAGE 1 — COVER =================
  doc.setFillColor(...COLORS.brand);
  doc.rect(0, 0, PAGE.w, 55, "F");

  doc.setTextColor(255, 255, 255);

  if (watermark) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    text(doc, "InsightPulse AI", PAGE.margin, 18);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  text(doc, TITLE, PAGE.margin, 34);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  text(doc, meta.datasetName || "Dataset", PAGE.margin, 44);

  doc.setTextColor(...COLORS.text);

  let y = 70;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.subtext);

  const infoLines = [
    ["Report Generated", fmtDate(meta.generatedAt)],
    ["Analysis Period", metrics.timePeriod ? `${metrics.timePeriod.start} - ${metrics.timePeriod.end}` : "N/A"],
    ["Records Analyzed", meta.rowCount.toLocaleString()],
    ["Columns", String(meta.columnCount)],
  ];

  infoLines.forEach(([label, value], i) => {
    const ry = y + i * 7;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.text);
    text(doc, label, PAGE.margin, ry);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.subtext);
    text(doc, value, PAGE.margin + 55, ry);
  });

  y += infoLines.length * 7 + 12;

  y = sectionHeading(doc, y, "Overview");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.text);
  y = wrapText(doc, buildScopeDescription(metrics), PAGE.margin, y, CONTENT_W, 5.5) + 8;

  y = sectionHeading(doc, y, "Analysis Scope");
  doc.setFontSize(9.5);

  const scopeRows = [
    ["Measures analyzed", meta.analysisScope.measures.map(humanize).join(", ") || "None detected"],
    ["Dimensions analyzed", meta.analysisScope.dimensions.map(humanize).join(", ") || "None detected"],
    ["Time dimension", meta.analysisScope.time.map(humanize).join(", ") || "None detected"],
  ];

  scopeRows.forEach(([label, value], i) => {
    const ry = y + i * 6;
    doc.setFont("helvetica", "bold");
    text(doc, label, PAGE.margin, ry);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.subtext);
    const lines = doc.splitTextToSize(safeText(value), CONTENT_W - 45);
    doc.text(lines, PAGE.margin + 45, ry);
    doc.setTextColor(...COLORS.text);
  });

  // ================= PAGE 2 — PERFORMANCE OVERVIEW =================
  doc.addPage();
  y = 24;
  y = sectionHeading(doc, y, "Performance Overview");

  const kpis = [];

  if (metrics.primary) {
    kpis.push({ label: `Total ${humanize(metrics.primary.measure)}`, value: fmtCompact(metrics.primary.total) });
    kpis.push({ label: "Total Records", value: fmtNumber(metrics.primary.count) });
    kpis.push({ label: `Average ${humanize(metrics.primary.measure)}`, value: fmtCompact(metrics.primary.average) });
  }

  if (metrics.repPerformance?.rows?.length) {
    kpis.push({ label: `Top ${humanize(metrics.repPerformance.dimension)}`, value: metrics.repPerformance.rows[0].label });
  }

  if (metrics.overallChange) {
    kpis.push({
      label: "Overall Change",
      value: metrics.overallChange.direction === "stable" ? "Stable" : fmtPercent(metrics.overallChange.pctChange),
      sublabel: `${metrics.overallChange.firstPeriod} to ${metrics.overallChange.lastPeriod}`,
      direction: metrics.overallChange.direction,
    });
  }

  y = drawKpiGrid(doc, y + 2, kpis, 3) + 6;

  if (metrics.overallChange && metrics.overallChange.direction !== "stable") {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...COLORS.subtext);
    const note = `${humanize(metrics.primary.measure)} ${directionWord(metrics.overallChange.direction).toLowerCase()} from ${fmtCompact(metrics.overallChange.first)} to ${fmtCompact(metrics.overallChange.last)} over the analysis period.`;
    y = wrapText(doc, note, PAGE.margin, y, CONTENT_W, 5) + 4;
  }

  // ================= PAGE 3 — KEY FINDINGS =================
  doc.addPage();
  y = 24;
  y = sectionHeading(doc, y, "Executive Insight and Key Findings");

  const topFindings = findings.slice(0, 5);

  topFindings.forEach((f) => {

    y = checkPageBreak(doc, y, 32);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.text);
    y = wrapText(doc, f.title, PAGE.margin, y, CONTENT_W, 5.5) + 1;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...COLORS.text);
    y = wrapText(doc, f.fact, PAGE.margin, y, CONTENT_W, 5) + 2;

    const interp = interpretations[f.id];

    if (interp) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...COLORS.brand);
      text(doc, "WHY IT MATTERS", PAGE.margin, y);
      y += 4.5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(...COLORS.subtext);
      y = wrapText(doc, interp, PAGE.margin, y, CONTENT_W, 5);
    }

    doc.setDrawColor(...COLORS.border);
    doc.line(PAGE.margin, y + 4, PAGE.w - PAGE.margin, y + 4);
    y += 10;
    doc.setTextColor(...COLORS.text);
  });

  // ================= PAGE 4 — TIME-BASED PERFORMANCE =================
  if (metrics.timeSeries?.length) {

    doc.addPage();
    y = 24;
    y = sectionHeading(doc, y, "Time-Based Performance Analysis");

    y = drawColumnChart(
      doc, PAGE.margin, y, CONTENT_W, 55,
      metrics.timeSeries.map(p => ({ label: p.period, value: p.value })),
    ) + 8;

    autoTable(doc, {
      startY: y,
      head: [["Period", `Total ${humanize(metrics.primary?.measure || "Value")}`, "Change vs Previous Period"]],
      body: metrics.timeSeries.map(p => [
        p.period,
        fmtCompact(p.value),
        p.absoluteChange === null ? "-" : `${fmtCompact(p.absoluteChange)} (${fmtPercent(p.pctChange)})`,
      ]),
      theme: "grid",
      headStyles: { fillColor: COLORS.brand, fontSize: 9 },
      styles: { fontSize: 9, textColor: COLORS.text },
      margin: { left: PAGE.margin, right: PAGE.margin },
    });

    y = doc.lastAutoTable.finalY + 8;

    const decomposition = findings.find(f => f.id === "trend_decomposition");

    if (decomposition) {
      y = checkPageBreak(doc, y, 20);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.subtext);
      y = wrapText(doc, decomposition.fact + " " + (interpretations[decomposition.id] || ""), PAGE.margin, y, CONTENT_W, 5);
      doc.setTextColor(...COLORS.text);
    }
  }

  // ================= PAGE 5 — REPRESENTATIVE PERFORMANCE =================
  if (metrics.repPerformance) {

    doc.addPage();
    y = 24;
    const repLabel = humanize(metrics.repPerformance.dimension);
    y = sectionHeading(doc, y, `${repLabel} Performance`);

    const rows = metrics.repPerformance.rows;
    const chartH = Math.min(rows.length * 10, 70);

    y = drawHorizontalBarChart(
      doc, PAGE.margin, y, CONTENT_W, chartH,
      rows.map(r => ({ label: r.label, value: r.value })),
    ) + 8;

    autoTable(doc, {
      startY: y,
      head: [["Rank", repLabel, `Total ${humanize(metrics.primary.measure)}`, "Share", "vs Top"]],
      body: rows.map(r => [
        r.rank,
        r.label,
        fmtCompact(r.value),
        `${fmtNumber(r.share)}%`,
        r.rank === 1 ? "-" : fmtPercent(r.diffFromTop),
      ]),
      theme: "grid",
      headStyles: { fillColor: COLORS.brand, fontSize: 9 },
      styles: { fontSize: 9 },
      margin: { left: PAGE.margin, right: PAGE.margin },
    });

    y = doc.lastAutoTable.finalY + 8;

    const c = metrics.repPerformance.concentration;
    y = checkPageBreak(doc, y, 24);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    text(doc, "Concentration", PAGE.margin, y);
    y += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.subtext);
    y = wrapText(doc, `Top 1: ${fmtNumber(c.top1Share)}%   |   Top 2: ${fmtNumber(c.top2Share)}%   |   Top 3: ${fmtNumber(c.top3Share)}%   |   ${repLabel} count: ${c.entityCount}`, PAGE.margin, y, CONTENT_W, 5) + 2;

    if (c.entityCount <= 5) {
      y = wrapText(
        doc,
        `Performance is highly concentrated within the observed dataset. If the organization has additional ${repLabel.toLowerCase()}s not included in this dataset, this concentration analysis may not represent the full sales organization.`,
        PAGE.margin, y, CONTENT_W, 5
      );
    }

    doc.setTextColor(...COLORS.text);
  }

  // ================= PAGE 6 — ADDITIONAL DIMENSION ANALYSIS =================
  if (metrics.dimensionBreakdowns?.length) {

    doc.addPage();
    y = 24;
    y = sectionHeading(doc, y, "Additional Dimension Analysis");

    metrics.dimensionBreakdowns.forEach((b) => {

      y = checkPageBreak(doc, y, 40);

      const label = humanize(b.dimension);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      text(doc, label, PAGE.margin, y);
      y += 6;

      autoTable(doc, {
        startY: y,
        head: [[label, `Total ${humanize(b.measure)}`, "Share"]],
        body: b.rows.slice(0, 5).map(r => [r.label, fmtCompact(r.value), `${fmtNumber(r.share)}%`]),
        theme: "striped",
        headStyles: { fillColor: COLORS.brandDark, fontSize: 8.5 },
        styles: { fontSize: 8.5 },
        margin: { left: PAGE.margin, right: PAGE.margin },
      });

      y = doc.lastAutoTable.finalY + 8;
    });
  }

  // ================= PAGE 7 — RECOMMENDATIONS =================
  doc.addPage();
  y = 24;
  y = sectionHeading(doc, y, "Recommendations");

  const recs = content.recommendations || [];

  if (!recs.length) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...COLORS.subtext);
    text(doc, "No specific recommendations were generated for this dataset.", PAGE.margin, y);
  }

  recs.forEach((r, i) => {

    y = checkPageBreak(doc, y, 40);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(...COLORS.brand);
    y = wrapText(doc, `Recommendation ${i + 1} - ${r.title}`, PAGE.margin, y, CONTENT_W, 5.5) + 2;

    const rows = [["Action", r.action], ["Reason", r.reason], ["Purpose", r.purpose]];

    rows.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(...COLORS.text);
      text(doc, label.toUpperCase(), PAGE.margin, y);
      y += 4.5;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(...COLORS.text);
      y = wrapText(doc, value || "", PAGE.margin, y, CONTENT_W, 5) + 3;
    });

    y += 3;
  });

  // ================= PAGE 8 — RECOMMENDED NEXT ANALYSIS =================
  doc.addPage();
  y = 24;
  y = sectionHeading(doc, y, "Recommended Next Analysis");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...COLORS.subtext);
  y = wrapText(doc, "The following questions cannot be answered from the current dataset but would add further business value if additional data were available.", PAGE.margin, y, CONTENT_W, 5) + 6;

  const questions = content.nextAnalysisQuestions || [];

  questions.forEach((q, i) => {
    y = checkPageBreak(doc, y, 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.text);
    y = wrapText(doc, `${i + 1}. ${q}`, PAGE.margin, y, CONTENT_W, 5.5) + 4;
  });

  drawFooters(doc, TITLE, { watermark });

  // Re-apply the running header to every page except the cover.
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 2; p <= pageCount; p++) {
    doc.setPage(p);
    drawHeader(doc, TITLE, meta.datasetName || "Dataset", { watermark });
  }

  return doc;
}
